/**
 * Preview data fetching hook
 * Handles fetching preview data for edit tools and email context.
 *
 * Failures set a per-key error flag (not just a swallowed log) so the approval
 * card can show an actionable "couldn't load preview / Retry" instead of a blank
 * card or a raw JSON dump the user would approve blind. In-flight requests are
 * aborted on unmount and superseded on retry.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthToken } from '../../lib/api/authenticated-fetch.js';
import { logger } from '../../lib/logger.js';

interface UsePreviewDataReturn {
  previewData: Record<string, any>;
  previewLoading: Record<string, boolean>;
  previewError: Record<string, boolean>;
  fetchPreviewData: (
    toolName: string,
    toolInput: any,
    approvalId: string,
    apiBaseUrl: string
  ) => Promise<void>;
}

export function usePreviewData(): UsePreviewDataReturn {
  const [previewData, setPreviewData] = useState<Record<string, any>>({});
  const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>({});
  const [previewError, setPreviewError] = useState<Record<string, boolean>>({});

  // Mirror previewData in a ref so the fetch guard doesn't re-create the callback
  // on every preview load (which would re-run the card's fetch effect).
  const previewDataRef = useRef(previewData);
  previewDataRef.current = previewData;

  // Track in-flight requests so we can abort on unmount / supersede on retry.
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((c) => c.abort());
      controllers.clear();
    };
  }, []);

  const fetchPreviewData = useCallback(
    async (toolName: string, toolInput: any, approvalId: string, apiBaseUrl: string) => {
      const cacheKey = `${toolName}-${approvalId}`;
      if (previewDataRef.current[cacheKey]) return; // Already fetched

      // Supersede any prior in-flight fetch for this key (e.g. a retry).
      controllersRef.current.get(cacheKey)?.abort();
      const controller = new AbortController();
      controllersRef.current.set(cacheKey, controller);

      setPreviewLoading((prev) => ({ ...prev, [cacheKey]: true }));
      setPreviewError((prev) => ({ ...prev, [cacheKey]: false }));
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        if (toolName === 'edit_contact' || toolName === 'edit_company') {
          const type = toolName === 'edit_contact' ? 'contact' : 'company';
          const response = await fetch(
            `${apiBaseUrl}/preview-entity?type=${type}&id=${toolInput.id}`,
            { headers, signal: controller.signal }
          );
          if (!response.ok) throw new Error(`preview-entity ${response.status}`);
          const current = await response.json();
          const updates =
            toolInput?.updates && typeof toolInput.updates === 'object'
              ? toolInput.updates
              : (() => {
                  const { id: _id, ...rest } = toolInput ?? {};
                  return rest;
                })();
          const proposed = { ...current, ...updates };
          setPreviewData((prev) => ({ ...prev, [cacheKey]: { current, proposed } }));
        } else if (toolName === 'bulk_update_tickets' || toolName === 'update_ticket') {
          // Resolve the ticket ids the tool will actually act on. Without this
          // the approval card shows only raw UUIDs, so "close these 14" is
          // unreviewable — and the chat text above it may list fewer tickets
          // than the call actually carries.
          const rawIds: unknown = toolInput?.ticket_ids ?? toolInput?.ticket_id;
          const ids: string[] = Array.isArray(rawIds)
            ? rawIds.filter((id): id is string => typeof id === 'string')
            : typeof rawIds === 'string'
              ? [rawIds]
              : [];
          if (ids.length === 0) {
            setPreviewData((prev) => ({
              ...prev,
              [cacheKey]: { tickets: [], requested: 0 },
            }));
          } else {
            const response = await fetch(`${apiBaseUrl}/preview-tickets`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ ids }),
              signal: controller.signal,
            });
            if (!response.ok) throw new Error(`preview-tickets ${response.status}`);
            const ticketPreview = await response.json();
            setPreviewData((prev) => ({ ...prev, [cacheKey]: ticketPreview }));
          }
        } else if (toolName === 'send_email' || toolName === 'send_email_with_template') {
          const response = await fetch(`${apiBaseUrl}/email-send-context`, {
            headers,
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`email-send-context ${response.status}`);
          const emailContext = await response.json();
          setPreviewData((prev) => ({ ...prev, [cacheKey]: { emailContext, input: toolInput } }));
        }
      } catch (error) {
        // Ignore aborts (unmount / superseded retry) — not user-facing failures.
        if (error instanceof DOMException && error.name === 'AbortError') return;
        logger.warn('failed to fetch preview data', {
          toolName,
          error: error instanceof Error ? error.message : String(error),
        });
        setPreviewError((prev) => ({ ...prev, [cacheKey]: true }));
      } finally {
        controllersRef.current.delete(cacheKey);
        setPreviewLoading((prev) => ({ ...prev, [cacheKey]: false }));
      }
    },
    []
  );

  return {
    previewData,
    previewLoading,
    previewError,
    fetchPreviewData,
  };
}
